import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RuleForm.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function RequiredRuleForm({ selectedField, rules, docType, docVersion, onRuleUpdate, showToast }) {
  const [formData, setFormData] = useState({
    type: 'string',
    severity: 'required',
    allowed_values: [],
    transaction_conditions: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [allowedValuesInput, setAllowedValuesInput] = useState('');
  const [showTransactionConditions, setShowTransactionConditions] = useState(false);
  const [transactionFlags, setTransactionFlags] = useState({
    buyer_count: '',
    seller_count: '',
    transaction_status: '',
    transaction_side: ''
  });

  useEffect(() => {
    if (selectedField && rules[selectedField.id]) {
      const existingRule = rules[selectedField.id];
      const conditions = existingRule.transaction_conditions || null;
      const fieldType = existingRule.type || selectedField.type || 'string';
      
      // For bool fields, default to ["True"] if no allowed_values set; normalize booleans to string "True"/"False"
      let defaultAllowedValues = existingRule.allowed_values || [];
      if (fieldType === 'bool' && (!existingRule.allowed_values || existingRule.allowed_values.length === 0)) {
        defaultAllowedValues = ['True'];
      } else if (fieldType === 'bool' && defaultAllowedValues.length > 0) {
        defaultAllowedValues = defaultAllowedValues.map(v =>
          v === true || v === 'true' ? 'True' : (v === false || v === 'false' ? 'False' : v)
        );
      }
      
      setFormData({
        type: fieldType,
        severity: existingRule.severity || 'required',
        allowed_values: defaultAllowedValues,
        transaction_conditions: conditions
      });
      setIsEditing(true);
      setAllowedValuesInput(JSON.stringify(defaultAllowedValues));
      
      // Parse transaction conditions
      if (conditions && (conditions.flags || conditions.transaction_status || conditions.transaction_side)) {
        setShowTransactionConditions(true);
        setTransactionFlags({
          buyer_count: conditions.flags?.buyer_count || '',
          seller_count: conditions.flags?.seller_count || '',
          transaction_status: conditions.transaction_status || '',
          transaction_side: conditions.transaction_side || ''
        });
      } else {
        setShowTransactionConditions(false);
        setTransactionFlags({ buyer_count: '', seller_count: '', transaction_status: '', transaction_side: '' });
      }
    } else {
      // Use field type from MongoDB (groupName) as default, fallback to 'string'
      const fieldType = selectedField?.type || 'string';
      // For bool fields, default allowed_values to ["True"]
      const defaultAllowedValues = fieldType === 'bool' ? ['True'] : [];
      
      setFormData({
        type: fieldType,
        severity: 'required',
        allowed_values: defaultAllowedValues,
        transaction_conditions: null
      });
      setIsEditing(false);
      setAllowedValuesInput(fieldType === 'bool' ? '["True"]' : '[]');
      setShowTransactionConditions(false);
      setTransactionFlags({ buyer_count: '', seller_count: '', transaction_status: '', transaction_side: '' });
    }
  }, [selectedField, rules]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const ruleData = {
        field_id: selectedField.id,
        type: formData.type,
        severity: formData.severity,
      };

      if (allowedValuesInput.trim()) {
        try {
          // Convert only unquoted True/False to JSON strings so ["True"] is stored
          let normalizedInput = allowedValuesInput
            .replace(/(\[|,)\s*True\s*(\]|,)/g, '$1"True"$2')
            .replace(/(\[|,)\s*False\s*(\]|,)/g, '$1"False"$2');
          ruleData.allowed_values = JSON.parse(normalizedInput);
        } catch (e) {
          showToast('Invalid JSON for allowed values', 'error');
          return;
        }
      }

      // Build transaction conditions from form
      if (showTransactionConditions) {
        const flags = {};
        if (transactionFlags.buyer_count) {
          flags.buyer_count = parseInt(transactionFlags.buyer_count) || transactionFlags.buyer_count;
        }
        if (transactionFlags.seller_count) {
          flags.seller_count = parseInt(transactionFlags.seller_count) || transactionFlags.seller_count;
        }
        
        const transactionConditions = {};
        if (Object.keys(flags).length > 0) {
          transactionConditions.flags = flags;
        }
        if (transactionFlags.transaction_status) {
          transactionConditions.transaction_status = transactionFlags.transaction_status;
        }
        if (transactionFlags.transaction_side) {
          transactionConditions.transaction_side = transactionFlags.transaction_side;
        }
        
        if (Object.keys(transactionConditions).length > 0) {
          ruleData.transaction_conditions = transactionConditions;
        }
      }

      const params = new URLSearchParams();
      if (docVersion) params.append('version', docVersion);
      await axios.post(`${API_BASE}/rules/${docType}/required?${params.toString()}`, ruleData);
      onRuleUpdate();
      showToast('Required rule saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving rule:', error);
      showToast('Error saving rule', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const params = new URLSearchParams();
      if (docVersion) params.append('version', docVersion);
      await axios.delete(`${API_BASE}/rules/${docType}/required/${selectedField.id}?${params.toString()}`);
      onRuleUpdate();
      showToast('Rule deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting rule:', error);
      showToast('Error deleting rule', 'error');
    }
  };

  return (
    <div className="rule-form">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Field Type</label>
          <select
            value={formData.type}
            onChange={(e) => {
              const newType = e.target.value;
              // If changing to bool, set allowed_values to ["True"]
              if (newType === 'bool' && formData.type !== 'bool') {
                setFormData({ 
                  ...formData, 
                  type: newType,
                  allowed_values: ['True']
                });
                setAllowedValuesInput('["True"]');
              } else if (newType !== 'bool' && formData.type === 'bool') {
                // If changing away from bool, clear allowed_values
                setFormData({ 
                  ...formData, 
                  type: newType,
                  allowed_values: []
                });
                setAllowedValuesInput('[]');
              } else {
                setFormData({ ...formData, type: newType });
              }
            }}
          >
            <option value="string">String</option>
            <option value="bool">Boolean</option>
            <option value="date">Date</option>
            <option value="list">List</option>
          </select>
        </div>

        <div className="form-group">
          <label>Severity</label>
          <select
            value={formData.severity}
            onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
          >
            <option value="required">Required</option>
            <option value="optional">Optional</option>
          </select>
        </div>

        <div className="form-group">
          <label>Allowed Values (JSON array, optional)</label>
          <textarea
            value={allowedValuesInput}
            onChange={(e) => setAllowedValuesInput(e.target.value)}
            placeholder='["True"] or ["value1", "value2"]'
            rows={3}
          />
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="show-transaction-conditions"
              checked={showTransactionConditions}
              onChange={(e) => setShowTransactionConditions(e.target.checked)}
            />
            <label htmlFor="show-transaction-conditions">Add Transaction Conditions</label>
          </div>
          
          {showTransactionConditions && (
            <div className="transaction-conditions-form">
              <div className="form-group">
                <label>Buyer Count</label>
                <input
                  type="number"
                  min="1"
                  value={transactionFlags.buyer_count}
                  onChange={(e) => setTransactionFlags({ ...transactionFlags, buyer_count: e.target.value })}
                  placeholder="e.g., 2"
                />
              </div>
              
              <div className="form-group">
                <label>Seller Count</label>
                <input
                  type="number"
                  min="1"
                  value={transactionFlags.seller_count}
                  onChange={(e) => setTransactionFlags({ ...transactionFlags, seller_count: e.target.value })}
                  placeholder="e.g., 2"
                />
              </div>
              
              <div className="form-group">
                <label>Transaction Status</label>
                <select
                  value={transactionFlags.transaction_status}
                  onChange={(e) => setTransactionFlags({ ...transactionFlags, transaction_status: e.target.value })}
                >
                  <option value="">Select transaction status</option>
                  <option value="UNDER_CONTRACT">UNDER_CONTRACT</option>
                  <option value="PRE_CONTRACT">PRE_CONTRACT</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Transaction Side</label>
                <select
                  value={transactionFlags.transaction_side}
                  onChange={(e) => setTransactionFlags({ ...transactionFlags, transaction_side: e.target.value })}
                >
                  <option value="">Select transaction side</option>
                  <option value="listing">Listing</option>
                  <option value="purchase">Purchase</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            {isEditing ? 'Update Rule' : 'Add Required Rule'}
          </button>
          {isEditing && (
            <button type="button" className="btn-danger" onClick={handleDelete}>
              Delete Rule
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default RequiredRuleForm;

